/**
 * @module M/control/GeometryDrawControlIhm
 */

import GeometryDrawImplControlIhm from 'impl/geometrydrawcontrolihm';
import template from 'templates/geometrydrawihm';
import drawingTemplate from 'templates/drawing';
import figureTemplate from 'templates/figure';
import locationDrawingTemplate from 'templates/locationdrawing';
import textDrawTemplate from 'templates/textdraw';
import textDrawTemplateihm from 'templates/textdrawihm';
import downloadingTemplate from 'templates/downloading';
import uploadingTemplate from 'templates/uploading';
import shpWrite from 'shp-write';
import * as shp from 'shpjs';
import { getValue } from './i18n/language';


const DEFAULT_TEXT = 'Texto';
const DEFAULT_FONT_COLOR = '#193063';
const DEFAULT_FONT_FAMILY = 'Arial';
const DEFAULT_SIZE = 12;

export default class GeometryDrawControlIhm extends M.Control {
  /**
   * @classdesc
   * Main constructor of the class. Creates a PluginControl
   * control
   *
   * @constructor
   * @extends {M.Control}
   * @api stable
   */
  constructor() {
    if (M.utils.isUndefined(GeometryDrawImplControlIhm)) {
      M.exception('La implementación usada no puede crear controles GeometryDrawControlIhm');
    }

    const impl = new GeometryDrawImplControlIhm();
    super(impl, 'GeometryDrawIhm');

    // facade control goes to impl as reference param
    impl.facadeControl = this;

    /**
     * Checks if point drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isPointActive = false;

    /**
     * Checks if located point drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isLocatedPointActive = false;

    /**
     * Checks if line drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isLineActive = false;

    /**
     * Checks if polygon drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isPolygonActive = false;

    /**
     * Checks if hole polygon drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isHoleActive = false;

    /**
     * Checks if rotate tool is active.
     * @private
     * @type {Boolean}
     */
    this.isRotateActive = false;

    /**
     * Checks if move tool is active.
     * @private
     * @type {Boolean}
     */
    this.isMoveActive = false;

    /**
     * Checks if scale tool is active.
     * @private
     * @type {Boolean}
     */
    this.isScaleActive = false;

    /**
     * Checks if text drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isTextActive = false;

    /**
     * Checks if text drawing tool is active.
     * @private
     * @type {Boolean}
     */
    this.isTextActiveihm = false;

    /**
     * Checks if edition tool is active.
     * @private
     * @type {Boolean}
     */
    this.isEditionActive = false;

    /**
     * Checks if figure tool is active.
     * @private
     * @type {Boolean}
     */
    this.isFigureActive = false;

    /**
     * Selected Mapea feature
     * @private
     * @type {M.feature}
     */
    this.feature = undefined;

    /**
     * Feature that is drawn on selection layer around this.feature
     * to emphasize it.
     * @private
     * @type {M.feature}
     */
    this.emphasis = undefined;

    /**
     * Current geometry type selected for drawing.
     * @private
     * @type {String}
     */
    this.geometry = undefined; // Point, LineString, Polygon

    /**
     * Template that expands drawing tools with color and thickness options.
     * @private
     * @type {String}
     */
    this.drawingTools = undefined;

    /**
     * Template that expands drawing tools with color and thickness options by location
     * @private
     * @type {String}
     */
    this.locationDrawingTools = undefined;

    /**
     * Template with downloading format options.
     * @private
     * @type {String}
     */
    this.downloadingTemplate = undefined;

    /**
     * Template with uploading format options.
     * @private
     * @type {String}
     */
    this.uploadingTemplate = undefined;

    /**
     * Template with text feature drawing tools.
     * @private
     * @type {String}
     */
    this.textDrawTemplate = undefined;

    /**
     * Template with text feature drawing tools.
     * @private
     * @type {String}
     */
    this.textDrawTemplateihm = undefined;

    /**
     * Current color for drawing features.
     * @private
     * @type {String}
     */
    this.currentColor = undefined;

    /**
     * Current line thickness (or circle radius) for drawing features.
     * @private
     * @type {Number}
     */
    this.currentThickness = undefined;

    /**
     * Current feature name / description text.
     * @private
     * @type {String}
     */
    this.textContent = 'Texto';

    /**
     * Current text feature font color.
     * @private
     * @type {String}
     */
    this.fontColor = '#F00';

    /**
     * Current text feature font size.
     * @private
     * @type {String}
     */
    this.fontSize = '12';

    /**
     * Current text feature font family.
     * @private
     * @type {String}
     */
    this.fontFamily = 'Verdana';

    /**
     * SRS of the input coordinates.
     * @private
     * @type {String}
     */
    this.srs = 'EPSG:4258';

    /**
     * Longitude of the input coordinates.
     * @private
     * @type {String}
     */
    this.x = undefined;

    /**
     * Latitude of the input coordinates.
     * @private
     * @type {String}
     */
    this.y = undefined;

    /**
     * Saves drawing layer ( __ draw__) from Mapea.
     * @private
     * @type {*} - Mapea layer
     */
    this.drawLayer = undefined;

    /**
     * File to upload.
     * @private
     * @type {*}
     */
    this.file_ = null;

    /**
     * Mapea layer where a square will be drawn around selected feature.
     * @private
     * @type {*}
     */
    this.selectionLayer = new M.layer.Vector({
      extract: false,
      name: 'selectLayer',
      source: this.getImpl().newVectorSource(true),
      displayInLayerSwitcher: false,
    });

    this.flagBlack = 'data:image/png;base64,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';
    this.flagRed = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH3wkLBigCQnNSHgAABchJREFUeNrtnU1v2zgQhocfsi1jD70U7f6EXhbbU9uccvD//0vpxVowzJAciaTXu3gewEjA2LHBecV5ZyTKIgAAAAAAAAAAAAAAAB3cbq+tMZeNufx5t9vru98r/8+JiFdeu73H9jd3u72G++9h5BgBfx8gbxRBc2ynCGLltZsYwu32utx/v95/Lp1jiCALRlSO8EeJwLVemwbyTeRtgAgc0T8Y3AkiKL12E8ZlC+QmgBEigL6AOxG5TBbBh6V/C/4AEZACBoggisgySgRZUNc8aJoAekRAwCct/VYRZAF0+dj2sxT45LWYwGcTQRagosPXjvpUAK3Hy8t3jwnsDH6tLKvk/xEiuGhH/Z7Hr19/e0xghwAUEfiR5aC2zKfm7mjgMYFzRTAqHcTSMt8TdEzghBQwUgSaQXsTefvx4y8/MvCYwCcRQX4k5mOjg44JnGQC94ggCfi5VL7NCjomcJwAXBLwqLRgdy/zjwo8JnCsCcxPwbZWgtBbvj2DCAi+fs7d1Ll7hsBjAsf6gJCuAIkwttXh9AwBxwQO9gDZT69cjLG0zsj9mw9MYGcKSLtzytK/libv27cv7hkEgAkcIwJRTKCrTJ5gAv8fwV9Khu+e/2M2ebFmDF9evi+YwP+eCE6l+j+7ImdtlIghrQ4etSpgAseUgV5pDLlS3jT0Cf451//z5/j+PyZwnABC2gFUghs7ROCTtLGIiHz9Kh4T+IQ+IF0BjJXAec9KkIpgRorABPabwCOVwKI8L82r3tJP6BUCJrBfBJq584kwmsuo1kqW9xs+QqupdHRVwASOMYFpJeAr+V+b0FQsQZlcl71HSJ7nS+cYrGLABPabQC2QsdUJvI/lBjLmY3eueeu5cYm4+epgTOAYE5gH0jqhUTGQiyIC0y7iLKjBmiIwgf0mMCrnBKwTmhrIk1JZaO8rxiqi2XLGBPaLQAvkxTiha6Vi8Jbg7hGBJgBM4BgTqAVyNU6oZiCvpVzfIwJNAJjAcSbwYqz/W5WAliJ86ajbIQJXSQGYwE4TmDd30rq+NaFWAxl3rgTqEo0JnGMC04lyxibQVTGQ1t7BkXTgMIFzTWBPOegsVxFlYhGlWSTJ/8uriIgJnGcCrx3l4LliAlti2T7KRUQ+ZyJQqwhM4DwT2FMOWs8dXCrL/KdGOgiYwPkmcD1QCexNG6dScAurU3UFwASON4F7K4EjJtA1XL922ziPCZxrAqMSoDDYBGorhiuUiEv2vIAJnGcClyTgIRNBTzu4evRlRrN5AgkTOMcE5nsCg7JZtDWhUTmaDy/LhT7BGRM4aRVQ9gSkY8vOvN5zX99Sn8BjAueawNLGkLwc9AVjeLSBpI2FQifwT0zg40SgXRgaK9VBPJA2amNan+APTOA8E+hKtbnWli00fFr7CawBqu5AwgTON4Ha/fq9sm2stVlk7RDBiU7gY0WgtWBdxRPESv4/cj1BrW3s0uXaaAJPmMB+E9i6d9BaqSKsJ4as6WDJbz9XMIFhR1eSFaAiAtcY85V87Xa2ks1jDROofZaz8v/OmECbCfSV1cErZwJdQQRxlAgMJlArV4PidyKHft0EuoowJCn9gvI8N7Ax1Lw/USVtBEXgLr/eARG0TaCrlHm+UTZ+2B3cIwKDCYyVgDvNVJL/653AWomoXQ4uytE3ojFkNYG+4WMEEewTgXbvwNIynwe8Z7fRERN4alQ0H0RA8Osm0CmB1CZUu8fAWRlbekVQSgGFbyVpfhchAmh3AhejT6hdE+AOXGO4ywSmm0cbRz0i2GECpdWbz7aSe6X0WxUzNqQSSD7/tjr4vSIg+O1OoM+O3NJ5Aq1E1MqyYT2BQooIiGC/CE6Nbl40uGqx3GN48tj2XUQfvgO59JkJvq3h4y2GqnAqORSuO5w6Jso3n5H/AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwMpvJwr+TFuvqZIAAAAASUVORK5CYII=';
    this.flagYellow = 'data:image/png;base64,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';
    this.flagGreen = 'data:image/png;base64,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';
    this.flagWhite = 'data:image/png;base64,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';
  }

  /**
   * This function creates the view
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  createView(map) {
    this.map = map;
    this.getImpl().setSource();
    return new Promise((success, fail) => {
      const html = M.template.compileSync(template, {
        vars: {
          translations: {
            punto: getValue('punto'),
            coordenadas: getValue('coordenadas'),
            linea: getValue('linea'),
            poligono: getValue('poligono'),
            texto: getValue('texto'),
            editgeometria: getValue('editgeometria'),
            descgeometria: getValue('descgeometria'),
            subircapa: getValue('subircapa'),
            limpiar: getValue('limpiar'),
            color: getValue('color'),
            grosor: getValue('grosor'),
            borrar: getValue('borrar'),
            rotation: getValue('rotation'),
            scale: getValue('scale'),
            move: getValue('move'),
            figura: getValue('figura'),
          },
        },
      });
      this.initializeLayers();
      this.createDrawingTemplate();
      this.createFigureTemplate();
      this.createTextDrawTemplate();
      this.createTextDrawTemplateihm();
      this.createDownloadingTemplate();
      this.createUploadingTemplate();
      this.addEvents(html);
      success(html);
    });
  }

  /**
   * Creates text drawing options template.
   * @public
   * @function
   * @api
   */
  createTextDrawTemplateihm() {
    this.textDrawTemplateihm = M.template.compileSync(textDrawTemplateihm, {
      jsonp: true,
      vars: {
        translations: {
          textoihm: getValue('textoihm'),
          position: getValue('position'),
          format: getValue('format'),
          add: getValue('add'),
          clean: getValue('clean'),
        },
        url: {
          flagBlack: this.flagBlack,
          flagRed: this.flagRed,
          flagYellow: this.flagYellow,
          flagGreen: this.flagGreen,
          flagWhite: this.flagWhite,
        },
      },
    });
    this.textDrawTemplateihm.querySelector('#capturar_pos').addEventListener('click', this.capturePosition.bind(this));
    this.textDrawTemplateihm.querySelector('#limpiar').addEventListener('click', () => this.cleanFeaturesihm());
    this.textDrawTemplateihm.querySelector('#addPos').addEventListener('click', () => this.addFlag());
  }

  capturePosition() {
    const text = this.textDrawTemplateihm.querySelector('#capturar_pos').innerHTML;
    if (text !== 'Desactivar pos') {
      this.textDrawTemplateihm.querySelector('#capturar_pos').innerHTML = 'Desactivar pos';
      this.getImpl().addDrawInteraction();
    } else {
      this.textDrawTemplateihm.querySelector('#capturar_pos').innerHTML = 'Activar pos';
      this.getImpl().removeDrawInteraction();
    }
  }

  /**
   * Creates text drawing options template.
   *
   * @public
   * @function
   * @api
   */
  createTextDrawTemplate() {
    this.textDrawTemplate = M.template.compileSync(textDrawTemplate, {
      jsonp: true,
      vars: {
        translations: {
          texto: getValue('texto'),
          fuente: getValue('fuente'),
          tamaño: getValue('tamaño'),
          color: getValue('color'),
          eliminar: getValue('eliminar'),
        },
      },
    });

    this.textContent = this.textDrawTemplate.querySelector('#textContent').value;
    this.fontColor = this.textDrawTemplate.querySelector('#fontColor').value;
    this.fontSize = this.textDrawTemplate.querySelector('#fontSize').value;
    this.fontFamily = this.textDrawTemplate.querySelector('#fontFamily').value;

    this.textDrawTemplate.querySelector('#textContent').addEventListener('input', e => this.styleChange(e));
    this.textDrawTemplate.querySelector('#fontColor').addEventListener('change', e => this.styleChange(e));
    this.textDrawTemplate.querySelector('#fontSize').addEventListener('change', e => this.styleChange(e));
    this.textDrawTemplate.querySelector('#fontFamily').addEventListener('change', e => this.styleChange(e));
    this.textDrawTemplate.querySelector('button').addEventListener('click', this.deleteSingleFeature.bind(this));
    this.textDrawTemplate.querySelector('button').style.display = 'none';
  }

  /**
   * Creates download options template.
   * @public
   * @function
   * @api
   */
  createDownloadingTemplate() {
    this.downloadingTemplate = M.template.compileSync(downloadingTemplate, {
      jsonp: true,
      vars: {
        translations: {
          descargar: getValue('descargar'),
        },
      },
    });
    this.downloadingTemplate.querySelector('button').addEventListener('click', this.downloadLayer.bind(this));
  }

  /**
   * Creates upload options template.
   *
   * @public
   * @function
   * @api
   */
  createUploadingTemplate() {
    const accept = '.kml, .zip, .gpx, .geojson';
    this.uploadingTemplate = M.template.compileSync(uploadingTemplate, {
      jsonp: true,
      vars: {
        accept,
        translations: {
          ficheros: getValue('ficheros'),
          seleccionar: getValue('seleccionar'),
          cargarcapa: getValue('cargarcapa'),
        },
      },
    });
    const inputFile = this.uploadingTemplate.querySelector('#geometrydrawihm-uploading>input');
    this.loadBtn_ = this.uploadingTemplate.querySelector('#geometrydrawihm-uploading button');
    inputFile.addEventListener('change', evt => this.changeFile(evt, inputFile.files[0]));
    this.loadBtn_.addEventListener('click', () => {
      this.loadLayer();
    });
    this.loadBtn_.setAttribute('disabled', 'disabled');
  }

  /**
   * Creates drawing options template.
   * @public
   * @function
   * @api
   */
  createDrawingTemplate() {
    this.drawingTools = M.template.compileSync(drawingTemplate, {
      jsonp: true,
      vars: {
        translations: {
          color: getValue('color'),
          grosor: getValue('grosor'),
          borrar: getValue('borrar'),
        },
      },
    });

    this.currentColor = this.drawingTools.querySelector('#colorSelector').value;
    this.currentThickness = this.drawingTools.querySelector('#thicknessSelector').value;

    this.drawingTools.querySelector('#colorSelector').addEventListener('change', e => this.styleChange(e));
    this.drawingTools.querySelector('#thicknessSelector').addEventListener('change', e => this.styleChange(e));
    this.drawingTools.querySelector('button').addEventListener('click', () => this.deleteSingleFeature());

    this.drawingTools.querySelector('button').style.display = 'none';
  }

  /**
   * Creates figure options template.
   * @public
   * @function
   * @api
   */
  createFigureTemplate() {
    this.figureTools = M.template.compileSync(figureTemplate, {
      jsonp: true,
      vars: {
        translations: {
          cuadrilatero: getValue('cuadrilatero'),
          circulo: getValue('circulo'),
          triangulo: getValue('triangulo'),
          pentagono: getValue('pentagono'),
          hexagono: getValue('hexagono'),
        },
      },
    });
    this.figureTools.querySelector('#figuresSelector').addEventListener('change', e => this.getImpl().changeFigure(e));
  }

  /**
   * Creates location drawing options template.
   * @public
   * @function
   * @api
   */
  createLocationDrawingTemplate() {
    const innerThis = this;
    this.locationDrawingTools = M.template.compileSync(locationDrawingTemplate, {
      jsonp: true,
      vars: {
        translations: {
          color: getValue('color'),
          grosor: getValue('grosor'),
          sistema: getValue('sistema'),
          longitud: getValue('longitud'),
          latitud: getValue('latitud'),
          dibujar: getValue('dibujar'),
          borrar: getValue('borrar'),
        },
      },
    });

    this.currentColor = this.locationDrawingTools.querySelector('#colorSelector').value;
    this.currentThickness = this.locationDrawingTools.querySelector('#thicknessSelector').value;

    this.locationDrawingTools.querySelector('#colorSelector').addEventListener('change', e => this.styleChange(e));
    this.locationDrawingTools.querySelector('#thicknessSelector').addEventListener('change', e => this.styleChange(e));
    this.locationDrawingTools.querySelector('button#m-geometrydrawihm-coordinates-delete').addEventListener('click', () => this.deleteSingleFeature());
    this.locationDrawingTools.querySelector('#m-geometrydrawihm-coordinates-srs').addEventListener('change', (e) => {
      const value = e.target.value;
      if (value === 'EPSG:4258' || value === 'EPSG:4326') {
        this.locationDrawingTools.querySelector('#m-geometrydrawihm-coordinates-utm').style.display = 'none';
        this.locationDrawingTools.querySelector('#m-geometrydrawihm-coordinates-latlon').style.display = 'block';
        this.locationDrawingTools.querySelector('#UTM-X').value = '';
        this.locationDrawingTools.querySelector('#UTM-Y').value = '';
      } else {
        this.locationDrawingTools.querySelector('#m-geometrydrawihm-coordinates-latlon').style.display = 'none';
        this.locationDrawingTools.querySelector('#m-geometrydrawihm-coordinates-utm').style.display = 'block';
        this.locationDrawingTools.querySelector('#LON').value = '';
        this.locationDrawingTools.querySelector('#LAT').value = '';
      }

      this.srs = value;
    });

    this.locationDrawingTools.querySelector('#LON').addEventListener('change', (e) => {
      const value = e.target.value;
      this.x = value;
    });

    this.locationDrawingTools.querySelector('#LAT').addEventListener('change', (e) => {
      const value = e.target.value;
      this.y = value;
    });

    this.locationDrawingTools.querySelector('#UTM-X').addEventListener('change', (e) => {
      const value = e.target.value;
      this.x = value;
    });

    this.locationDrawingTools.querySelector('#UTM-Y').addEventListener('change', (e) => {
      const value = e.target.value;
      this.y = value;
    });

    this.locationDrawingTools.querySelector('button#m-geometrydrawihm-coordinates-draw').addEventListener('click', () => {
      const newPointStyle = new M.style.Point({
        radius: innerThis.currentThickness,
        fill: {
          color: innerThis.currentColor,
        },
        stroke: {
          color: 'white',
          width: 2,
        },
      });

      if (innerThis.x !== undefined && innerThis.x.length > 0 &&
        innerThis.y !== undefined && innerThis.y.length > 0) {
        const parsedX = parseFloat(innerThis.x);
        const parsedY = parseFloat(innerThis.y);
        if (!Number.isNaN(parsedX) && !Number.isNaN(parsedY)) {
          const coordinates = [parsedX, parsedY];
          innerThis.feature = innerThis.getImpl().drawPointFeature(coordinates, innerThis.srs);
          innerThis.feature.setStyle(newPointStyle);
        } else {
          M.dialog.error(getValue('exception.formatocoord'));
        }
      } else {
        M.dialog.error(getValue('exception.introduzcoord'));
      }
    });
  }

  /**
   * Adds event listeners to geometry buttons.
   * @public
   * @function
   * @api
   * @param {String} html - Geometry buttons template.
   */
  addEvents(html) {
    html.querySelector('#pointdrawing').addEventListener('click', e => this.geometryBtnClick('Point'));
    html.querySelector('#pointlocateddrawing').addEventListener('click', e => this.geometryBtnClick('LocatedPoint'));
    html.querySelector('#linedrawing').addEventListener('click', e => this.geometryBtnClick('LineString'));
    html.querySelector('#polygondrawing').addEventListener('click', e => this.geometryBtnClick('Polygon'));
    html.querySelector('#holedrawing').addEventListener('click', e => this.geometryBtnClick('Hole'));
    html.querySelector('#figuredrawing').addEventListener('click', e => this.geometryBtnClick('Figure'));
    html.querySelector('#textdrawing').addEventListener('click', e => this.geometryBtnClick('Text'));
    html.querySelector('#textdrawingihm').addEventListener('click', e => this.geometryBtnClick('Textihm'));
    html.querySelector('#rotatedrawing').addEventListener('click', e => this.geometryBtnClick('Rotate'));
    html.querySelector('#movedrawing').addEventListener('click', e => this.geometryBtnClick('Move'));
    html.querySelector('#scaledrawing').addEventListener('click', e => this.geometryBtnClick('Scale'));
    html.querySelector('#cleanAll').addEventListener('click', () => this.deleteDrawnFeatures());
    html.querySelector('#download').addEventListener('click', () => this.openDownloadOptions());
    html.querySelector('#upload').addEventListener('click', () => this.openUploadOptions());
    html.querySelector('#edit').addEventListener('click', () => this.editBtnClick());
  }

  /**
   * Hides/shows tools menu and de/activates drawing.
   * @public
   * @function
   * @api
   * @param {Boolean} clickedGeometry - i.e.isPointActive
   * @param {String} drawingDiv - i.e.pointdrawing
   * @param {String} geometry - geometry type
   */
  activationManager(clickedGeometry, drawingDiv, geometry) {
    // if drawing is active
    if (this[clickedGeometry]) {
      this.deactivateDrawing();
      this.geometry = undefined;
      this[clickedGeometry] = false;
      document.getElementById(drawingDiv).classList.remove('activeTool');
    } else {
      this.deactivateDrawing();
      this[clickedGeometry] = true;
      this.geometry = geometry;
      document.getElementById(drawingDiv).classList.add('activeTool');

      if (document.getElementById('drawingtools') !== null) {
        document.getElementById('drawingtools').remove();
      } else if (document.getElementById('textdrawtools') !== null) {
        document.getElementById('textdrawtools').remove();
      } else if (document.getElementById('textdrawtoolsihm') !== null) {
        document.getElementById('textdrawtoolsihm').remove();
      } else if (document.getElementById('geometrydrawihm-uploading') !== null) {
        document.getElementById('geometrydrawihm-uploading').remove();
      }
    }
  }

  /**
   * Shows/hides tools template and
   * adds/removes draw interaction.
   * @public
   * @function
   * @api
   * @param {String} geometry - clicked button geometry type
   */
  geometryBtnClick(geometry) {
    if (this.isEditionActive) {
      this.deactivateEdition();
      document.querySelector('#otherBtns>#edit').classList.remove('activeTool');
    }

    switch (geometry) {
      case 'Point':
        this.activationManager('isPointActive', 'pointdrawing', geometry);
        break;
      case 'LocatedPoint':
        this.activationManager('isLocatedPointActive', 'pointlocateddrawing', geometry);
        break;
      case 'LineString':
        this.activationManager('isLineActive', 'linedrawing', geometry);
        break;
      case 'Polygon':
        this.activationManager('isPolygonActive', 'polygondrawing', geometry);
        break;
      case 'Hole':
        this.activationManager('isHoleActive', 'holedrawing', 'Polygon');
        break;
      case 'Figure':
        this.activationManager('isFigureActive', 'figuredrawing', 'Polygon');
        break;
      case 'Text':
        this.activationManager('isTextActive', 'textdrawing', 'Point');
        break;
      case 'Textihm':
        this.activationManager('isTextActiveihm', 'textdrawingihm', 'Point');
        break;
      case 'Rotate':
        this.activationManager('isRotateActive', 'rotatedrawing', 'Polygon');
        break;
      case 'Move':
        this.activationManager('isMoveActive', 'movedrawing', 'Polygon');
        break;
      case 'Scale':
        this.activationManager('isScaleActive', 'scaledrawing', 'Polygon');
        break;
      default:
    }

    if (geometry === 'LocatedPoint') {
      this.createLocationDrawingTemplate();
    } else {
      this.createDrawingTemplate();
    }

    if (this.isPointActive || this.isLocatedPointActive || this.isLineActive ||
      this.isPolygonActive || this.isHoleActive || this.isTextActive ||
      this.isTextActiveihm || this.isRotateActive || this.isMoveActive ||
      this.isScaleActive || this.isFigureActive) {
      if (this.isTextActive) {
        this.textDrawTemplate.querySelector('#textContent').value = '';
        this.textDrawTemplate.querySelector('button').style.display = 'none';
        document.querySelector('.m-geometrydrawihm').appendChild(this.textDrawTemplate);
      } else if (this.isTextActiveihm) {
        document.querySelector('.m-geometrydrawihm').appendChild(this.textDrawTemplateihm);
        this.textDrawTemplateihm.querySelector('#textContentihm').value = '';
        this.textDrawTemplateihm.querySelector('#textContentpos').value = '';
        this.textDrawTemplateihm.querySelector('#capturar_pos').innerHTML = 'Activar pos';
      } else if (this.isLocatedPointActive) {
        document.querySelector('.m-geometrydrawihm').appendChild(this.locationDrawingTools);
      } else if (this.isFigureActive) {
        document.querySelector('.m-geometrydrawihm').appendChild(this.figureTools);
      } else if (geometry !== 'Hole' && geometry !== 'Rotate' && geometry !== 'Scale' && geometry !== 'Move') {
        this.drawingTools.querySelector('button').style.display = 'none';
        document.querySelector('.m-geometrydrawihm').appendChild(this.drawingTools);
      }

      if (geometry === 'Hole') {
        this.getImpl().addHoleDrawInteraction();
      } else if (geometry === 'Rotate') {
        this.getImpl().addRotateInteraction();
      } else if (geometry === 'Move') {
        this.getImpl().addMoveInteraction();
      } else if (geometry === 'Scale') {
        this.getImpl().addScaleInteraction();
      } else if (geometry === 'Figure') {
        this.getImpl().addFigureInteraction();
      } else if (geometry !== 'LocatedPoint' && geometry !== 'Textihm') {
        this.getImpl().addDrawInteraction();
      }

      if (document.querySelector('#drawingtools #featureInfo') !== null) {
        document.querySelector('#drawingtools #featureInfo').style.display = 'none';
      }

      if (document.querySelector('.m-geometrydrawihm>#downloadFormat')) {
        document.querySelector('.m-geometrydrawihm').removeChild(this.downloadingTemplate);
      }
    }
  }

  /**
   * Checks if any drawing button is active and deactivates it,
   * deleting drawing interaction.
   * @public
   * @function
   * @api
   */
  deactivateDrawing() {
    if (this.isPointActive || this.isLocatedPointActive || this.isLineActive ||
      this.isPolygonActive || this.isHoleActive || this.isTextActive ||
      this.isTextActiveihm || this.isRotateActive || this.isMoveActive ||
      this.isScaleActive || this.isFigureActive) {
      if (this.isLocatedPointActive) {
        document.querySelector('.m-geometrydrawihm').removeChild(this.locationDrawingTools);

        this.srs = 'EPSG:4258';
        this.x = undefined;
        this.y = undefined;
      } else {
        if (document.querySelector('.m-geometrydrawihm #drawingtools') !== null) {
          document.querySelector('.m-geometrydrawihm').removeChild(this.drawingTools);
        } else if (document.querySelector('.m-geometrydrawihm #figuretools') !== null) {
          document.querySelector('.m-geometrydrawihm').removeChild(this.figureTools);
        } else if (document.querySelector('.m-geometrydrawihm #textdrawtools') !== null) {
          document.querySelector('.m-geometrydrawihm').removeChild(this.textDrawTemplate);
        } else if (document.querySelector('.m-geometrydrawihm #textdrawtoolsihm') !== null) {
          document.querySelector('.m-geometrydrawihm').removeChild(this.textDrawTemplateihm);
        }
        this.getImpl().removeDrawInteraction();
        this.getImpl().removeDrawHoleInteraction();
        this.getImpl().removeRotateInteraction();
        this.getImpl().removeMoveInteraction();
        this.getImpl().removeScaleInteraction();
        this.getImpl().removeFigureDrawInteraction();
      }
      this.selectionLayer.removeFeatures([this.emphasis]);
      if ((this.feature !== undefined) && this.feature.getStyle().get('label') !== undefined) {
        this.feature.getStyle().set('fill.opacity', 0);
      }

      this.feature = undefined;
      this.isPointActive = false;
      this.isLocatedPointActive = false;
      this.isLineActive = false;
      this.isPolygonActive = false;
      this.isHoleActive = false;
      this.isFigureActive = false;
      this.isRotateActive = false;
      this.isMoveActive = false;
      this.isScaleActive = false;
      this.isTextActive = false;
      this.isTextActiveihm = false;
      document.getElementById('pointdrawing').classList.remove('activeTool');
      document.getElementById('pointlocateddrawing').classList.remove('activeTool');
      document.getElementById('linedrawing').classList.remove('activeTool');
      document.getElementById('polygondrawing').classList.remove('activeTool');
      document.getElementById('holedrawing').classList.remove('activeTool');
      document.getElementById('figuredrawing').classList.remove('activeTool');
      document.getElementById('rotatedrawing').classList.remove('activeTool');
      document.getElementById('movedrawing').classList.remove('activeTool');
      document.getElementById('scaledrawing').classList.remove('activeTool');
      document.getElementById('textdrawing').classList.remove('activeTool');
      document.getElementById('textdrawingihm').classList.remove('activeTool');
    }
  }

  /**
   * Deactivates edition tool.
   * @public
   * @function
   * @api
   */
  deactivateEdition() {
    if (this.isEditionActive) {
      this.isEditionActive = false;
      this.deactivateSelection();
      if (document.querySelector('#drawingtools #featureInfo') !== null) {
        document.querySelector('#drawingtools #featureInfo').style.display = 'none';
      }
    }
  }

  /**
   * Activates or deactivates feature selection & modify interaction.
   * Deactivates drawing if it's active.
   * @public
   * @function
   * @api
   */
  editBtnClick() {
    if (this.isEditionActive) {
      this.deactivateEdition();
      document.querySelector('#otherBtns>#edit').classList.remove('activeTool');
      this.isEditionActive = false;
    } else {
      this.deactivateDrawing();
      if (document.getElementById('geometrydrawihm-uploading') !== null) {
        document.getElementById('geometrydrawihm-uploading').remove();
      }
      this.getImpl().activateSelection();
      this.isEditionActive = true;
      document.querySelector('#otherBtns>#edit').classList.add('activeTool');
    }
  }

  /**
   * Changes style of current feature.
   * @public
   * @function
   * @api
   * @param {Event} e - input on.change
   */
  styleChange(e) {
    if (this.feature) {
      const evtId = e.target.id;

      if (document.querySelector('#colorSelector') !== null) {
        this.currentColor = document.querySelector('#colorSelector').value;
        this.currentThickness = document.querySelector('#thicknessSelector').value;
      } else {
        this.textContent = document.querySelector('#textContent').value;
        this.fontColor = document.querySelector('#fontColor').value;
        this.fontSize = document.querySelector('#fontSize').value;
        this.fontFamily = document.querySelector('#fontFamily').value;
      }

      switch (this.feature.getGeometry().type) {
        case 'Point':
        case 'MultiPoint':
          if (evtId === 'colorSelector' || evtId === 'thicknessSelector') {
            const newPointStyle = new M.style.Point({
              radius: this.currentThickness,
              fill: {
                color: this.currentColor,
              },
              stroke: {
                color: 'white',
                width: 2,
              },
            });
            if (this.feature !== undefined) this.feature.setStyle(newPointStyle);
          } else {
            this.setTextStyle(false);
            if (this.isTextActiveihm) {
              this.setTextStyleBandera();
            }
          }
          break;
        case 'LineString':
        case 'MultiLineString':
          const newLineStyle = new M.style.Line({
            stroke: {
              color: this.currentColor,
              width: this.currentThickness,
            },
          });
          if (this.feature !== undefined) this.feature.setStyle(newLineStyle);
          break;
        case 'Polygon':
        case 'MultiPolygon':
          const newPolygonStyle = new M.style.Polygon({
            fill: {
              color: this.currentColor,
              opacity: 0.2,
            },
            stroke: {
              color: this.currentColor,
              width: this.currentThickness,
            },
          });
          if (this.feature !== undefined) this.feature.setStyle(newPolygonStyle);
          break;
        default:
          break;
      }
    } else if (document.querySelector('#colorSelector') !== null) {
      this.currentColor = document.querySelector('#colorSelector').value;
      this.currentThickness = document.querySelector('#thicknessSelector').value;
    }
  }

  /**
   * Updates input values with selected feature values.
   * @public
   * @function
   * @api
   */
  updateInputValues() {
    if (this.feature) {
      const featureStyle = this.feature.getStyle();
      let featureIsText;

      if (featureStyle !== null) {
        featureIsText = featureStyle.get('label') !== undefined;
      }

      this.geometry = this.feature.getGeometry().type;
      if (!featureStyle.getOptions().icon) {
        if (featureIsText) {
          const fontProps = featureStyle.get('label.font').split(' ');
          this.fontColor = featureStyle.get('label.color');
          this.textContent = featureStyle.get('label.text');
          if (fontProps[0] === 'bold') {
            this.fontSize = fontProps[1].slice(0, fontProps[1].length - 2);
            this.fontFamily = fontProps[2];
          } else {
            this.fontSize = fontProps[0].slice(0, fontProps[0].length - 2);
            this.fontFamily = fontProps[1];
          }
          this.updateTextInputValues(
            this.textContent,
            this.fontColor,
            this.fontSize,
            this.fontFamily,
          );
        } else if (!this.isTextActiveihm) {
          this.currentColor = this.getFeatureColor();
          this.currentThickness = this.getFeatureThickness();
          this.drawingTools.querySelector('#colorSelector').value = this.currentColor;
          this.drawingTools.querySelector('#thicknessSelector').value = this.currentThickness;
          document.querySelector('#drawingtools #colorSelector').value = this.currentColor;
          document.querySelector('#drawingtools #thicknessSelector').value = this.currentThickness;
        }
      }
    }
  }

  /**
   * Updates TextDraw menu options with given values.
   * @public
   * @function
   * @api
   * @param {String} content - #textContent
   * @param {String} color - #fontColor
   * @param {String} size - #fontSize
   * @param {String} family - #fontFamily
   */
  updateTextInputValues(content, color, size, family) {
    this.textDrawTemplate.querySelector('#textContent').value = content;
    this.textDrawTemplate.querySelector('#fontColor').value = color;
    this.textDrawTemplate.querySelector('#fontSize').value = size;
    this.textDrawTemplate.querySelector('#fontFamily').value = family;
    document.querySelector('#textdrawtools #textContent').value = content;
    document.querySelector('#textdrawtools #fontColor').value = color;
    document.querySelector('#textdrawtools #fontSize').value = size;
    document.querySelector('#textdrawtools #fontFamily').value = family;
  }

  /**
   * Updates global text drawing variables with current input values.
   * @public
   * @function
   * @api
   */
  updateGlobalsWithInput() {
    const textInput = document.querySelector('#textdrawtools #textContent');
    this.textContent = textInput.value === '' ? 'Texto' : textInput.value;
    this.fontColor = document.querySelector('#textdrawtools #fontColor').value;
    this.fontSize = document.querySelector('#textdrawtools #fontSize').value;
    this.fontFamily = document.querySelector('#textdrawtools #fontFamily').value;
  }

  updateGlobalInputBandera() {
    const textInput = document.querySelector('#textdrawtoolsihm #textContentihm');
    this.textContentihm = textInput.value === '' ? '' : textInput.value;
    const graficoInput = document.getElementsByName('SelectFlag');
    graficoInput.forEach((elem) => {
      if (elem.checked) {
        this.flagIcon = elem.value;
        this.flagcolor = elem.id;
      }
    });
  }

  /**
   * Adds style and source to vector layer.
   * Adds selection layer to map.
   * @public
   * @function
   * @api
   */
  initializeLayers() {
    this.drawLayer = this.map.getLayers()[this.map.getLayers().length - 1];
    // popup desactivated
    this.map.getLayers()[this.map.getLayers().length - 1].getImpl().extract = false;
    this.selectionLayer.displayInLayerSwitcher = false;
    this.map.addLayers(this.selectionLayer);
    this.selectionLayer.setZIndex(this.selectionLayer.getZIndex() + 8);
    this.getImpl().setImplSource();
  }


  /**
   * Sets style for text feature.
   * @public
   * @function
   * @api
   * @param {Boolean} defaultTextStyle - whether default style should be used
   */
  setTextStyle(defaultTextStyle) {
    let fontProperties;

    if (defaultTextStyle) {
      this.textContent = DEFAULT_TEXT;
      this.fontColor = DEFAULT_FONT_COLOR;
      this.fontFamily = DEFAULT_FONT_FAMILY;
      this.fontSize = DEFAULT_SIZE;
    }

    if ((this.feature.getStyle() !== null) &&
      this.feature.getStyle().get('label.font').split(' ')[0] === 'bold') {
      fontProperties = `bold ${this.fontSize}px ${this.fontFamily}`;
    } else {
      fontProperties = `${this.fontSize}px ${this.fontFamily}`;
    }

    this.feature.setStyle(new M.style.Point({
      radius: 3,
      fill: {
        color: '#ff5733',
        opacity: 1,
      },
      stroke: {
        color: '#fdfefe',
        width: 0,
      },
      label: {
        text: this.textContent,
        font: fontProperties,
        color: this.fontColor,
      },
    }));
  }

  /**
   * Sets style for text feature.
   * @public
   * @function
   * @api
   * @param {Boolean} defaultTextStyle - whether default style should be used
   */
  setTextStyleBandera() {
    this.feature.setStyle(new M.style.Point({
      label: {
        text: this.textContentihm,
        align: M.style.align.LEFT,
        color: this.flagcolor,
      },
      icon: {
        src: this.flagIcon,
        anchor: [0.5, 0.6],
        scale: 0.5,
      },
    }));
  }

  /**
   * Checks if textdrawtool box has default input values.
   * @public
   * @function
   * @api
   */
  defaultTextInputValues() {
    const defaults = ['', DEFAULT_FONT_COLOR, DEFAULT_SIZE, DEFAULT_FONT_FAMILY];
    const inputs = document.querySelectorAll('#textdrawtools>label>*');
    let sameStyles = true;
    for (let i = 0; i < inputs.length; i += 1) {
      if (inputs[i].value !== defaults[i]) sameStyles = false;
    }
    return sameStyles;
  }

  /**
   * Sets style for a point, line or polygon feature
   * change de style of shapefiles and kml files
   * @public
   * @function
   * @api
   * @param {*} feature
   * @param {*} geometryType - Point / LineString / Polygon
   */
  setFeatureStyle(feature, geometryType) {
    switch (geometryType) {
      case 'Point':
      case 'MultiPoint':
        feature.setStyle(new M.style.Point({
          radius: this.currentThickness,
          fill: {
            color: this.currentColor,
          },
          stroke: {
            color: 'white',
            width: 2,
          },
        }));
        break;
      case 'LineString':
      case 'MultiLineString':
        feature.setStyle(new M.style.Line({
          stroke: {
            color: this.currentColor,
            width: this.currentThickness,
          },
        }));
        break;
      case 'Polygon':
      case 'MultiPolygon':
        feature.setStyle(new M.style.Polygon({
          fill: {
            color: this.currentColor,
            opacity: 0.2,
          },
          stroke: {
            color: this.currentColor,
            width: Number.parseInt(this.currentThickness, 10),
          },
        }));
        break;
      default:
        throw new Error(getValue('exception.geometrianoencontrar'));
    }
  }

  /**
   * After draw interaction event is over,
   * updates feature style, inputs, adds feature to draw layer,
   * shows feature info and emphasizes it.
   * @public
   * @function
   * @api
   * @param {Event} event - 'drawend' triggering event
   */
  onDraw(event) {
    const lastFeature = this.feature;
    this.hideTextPoint();
    this.feature = event.feature;
    this.feature = M.impl.Feature.olFeature2Facade(this.feature);
    if (this.geometry === undefined) this.geometry = this.feature.getGeometry().type;

    if (this.geometry === 'Point' && (this.isTextActive || this.isTextActiveihm)) {
      if (this.isTextActive) {
        this.updateGlobalsWithInput();
        if (lastFeature !== undefined) this.textContent = 'Texto';
        this.setTextStyle(false);
      } else {
        this.updateGlobalInputBandera();
        this.setTextStyleBandera();
      }
    } else {
      this.setFeatureStyle(this.feature, this.geometry);
    }

    if (this.isTextActive) {
      document.querySelector('.m-geometrydrawihm #textdrawtools button').style.display = 'block';
    }

    this.map.getLayers()[this.map.getLayers().length - 1].addFeatures(this.feature);

    this.emphasizeSelectedFeature();
    this.showFeatureInfo();
    if (!this.isTextActiveihm) {
      this.updateInputValues();
    }
  }

  /**
   * @public
   * @function
   * @api
   * @param {Event} event - 'drawend' triggering event
   */
  onDrawFigure(event) {
    this.feature = event.feature;
    this.feature = M.impl.Feature.olFeature2Facade(this.feature);
    this.setFeatureStyle(this.feature, this.geometry);
    this.map.getLayers()[this.map.getLayers().length - 1].addFeatures(this.feature);
  }

  /**
   * Deletes Mapea feature set attributes.
   * @public
   * @function
   * @api
   * @param {Array<M.Feature>} features
   */
  deleteFeatureAttributes(features) {
    const newFeatures = features;
    // newFeatures.forEach((feature) => {
    this.getImpl().unsetAttributes(newFeatures);
    // });
    return newFeatures;
  }

  /**
   * On select, shows feature info.
   * @public
   * @function
   * @api
   */
  showFeatureInfo() {
    const infoContainer = document.querySelector('#drawingtools #featureInfo');
    const inputPosicion = document.querySelector('#textdrawtoolsihm #textContentpos');
    let selectLocation = document.querySelector('#textdrawtoolsihm #formatihm');
    if (selectLocation != null) {
      selectLocation = document.querySelector('#textdrawtoolsihm #formatihm').value;
    }
    if (infoContainer !== null) {
      infoContainer.style.display = 'block';
      infoContainer.innerHTML = '';
    }

    switch (this.geometry) {
      case 'Point':
      case 'MultiPoint':
        const x = this.getImpl().getFeatureCoordinates()[0];
        const y = this.getImpl().getFeatureCoordinates()[1];
        if (infoContainer !== null) {
          infoContainer.innerHTML = `Coordenadas<br/>
          x: ${Math.round(x * 1000) / 1000},<br/>
          y: ${Math.round(y * 1000) / 1000}`;
        } else if (inputPosicion !== null) {
          // Si esta marcada segunda opcion de grados
          const coor = this.getImpl().getFeatureCoordinates();
          const code = this.map_.getProjection().code;
          const newCoordinates = this.getImpl().getTransformedCoordinates(code, coor);
          if (selectLocation === 'grados') {
            inputPosicion.value = `${newCoordinates[1].toFixed(5)}, ${newCoordinates[0].toFixed(5)}`;
          } else {
            const latgr = Number.parseInt(Math.abs(newCoordinates[1]), 10);
            const latmin = (Math.abs(newCoordinates[1]) - latgr) * 60;
            const NS = newCoordinates[1] > 0 ? 'N' : 'S';
            const longr = Number.parseInt(Math.abs(newCoordinates[0]), 10);
            const lonmin = (Math.abs(newCoordinates[0]) - longr) * 60;
            const EW = newCoordinates[0] > 0 ? 'E' : 'W';
            inputPosicion.value = `${latgr.toString()} ${latmin.toFixed(3).toString()} ${NS},${longr.toString()} ${lonmin.toFixed(3).toString()} ${EW}`;
          }
        }
        break;
      case 'LineString':
      case 'MultiLineString':
        let lineLength = this.getImpl().getFeatureLength();
        let units = 'km';
        if (lineLength > 100) {
          lineLength = Math.round((lineLength / 1000) * 100) / 100;
        } else {
          lineLength = Math.round(lineLength * 100) / 100;
          units = 'm';
        }
        if (infoContainer !== null) infoContainer.innerHTML = `Longitud: ${lineLength} ${units}`;
        break;
      case 'Polygon':
      case 'MultiPolygon':
        let area = this.getImpl().getFeatureArea();
        let areaUnits = `km${'2'.sup()}`;
        if (area > 10000) {
          area = Math.round((area / 1000000) * 100) / 100;
        } else {
          area = Math.round(area * 100) / 100;
          areaUnits = `m${'2'.sup()}`;
        }
        if (infoContainer !== null) infoContainer.innerHTML = `Área: ${area} ${areaUnits}`;
        break;
      default:
        if (document.querySelector('#drawingtools #featureInfo') !== null) {
          document.querySelector('#drawingtools #featureInfo').style.display = 'none';
        }
        break;
    }
  }

  /**
   * Clears selection layer.
   * Draws square around feature and adds it to selection layer.
   * For points:
   *    If feature doesn't have style, sets new style.
   * For text:
   *    Colours red text feature point.
   * @public
   * @function
   * @api
   */
  emphasizeSelectedFeature() {
    this.emphasis = null;
    this.selectionLayer.removeFeatures(this.selectionLayer.getFeatures());

    if (this.feature) {
      const isTextDrawActive = document.querySelector('#textdrawtools') !== null;

      // if point vs text vs else
      if ((this.geometry === 'Point' || this.geometry === 'MultiPoint') && !isTextDrawActive && !this.isTextActiveihm) {
        this.emphasis = this.getImpl().getMapeaFeatureClone();

        this.emphasis.setStyle(new M.style.Point({
          radius: 20,
          stroke: {
            color: '#FF0000',
            width: 2,
          },
        }));
      } else if ((this.geometry === 'Point' || this.geometry === 'MultiPoint') && isTextDrawActive) {
        if (this.feature.getStyle() === null) this.setTextStyle(false);
        this.feature.getStyle().set('fill.opacity', 1);
      } else if (!this.isTextActiveihm) {
        // eslint-disable-next-line no-underscore-dangle
        const extent = this.getImpl().getFeatureExtent();
        this.emphasis = M.impl.Feature.olFeature2Facade(this.getImpl().newPolygonFeature(extent));
        this.emphasis.setStyle(new M.style.Line({
          stroke: {
            color: '#FF0000',
            width: 2,
          },
        }));
      }
      this.selectionLayer.displayInLayerSwitcher = false;
      this.selectionLayer.addFeatures([this.emphasis]);
    }
  }

  /**
   * Hides point associated to text feature.
   * @public
   * @function
   * @api
   */
  hideTextPoint() {
    if (this.geometry === 'Point' &&
      this.feature &&
      this.feature.getStyle().get('label') !== undefined) {
      this.feature.getStyle().set('fill.opacity', 0);
    }
  }

  /**
   * Opens download template
   * @public
   * @function
   * @api
   */
  openDownloadOptions() {
    if (this.drawLayer.getFeatures().length !== 0) {
      if (this.isEditionActive) {
        this.deactivateEdition();
        document.querySelector('#otherBtns>#edit').classList.remove('activeTool');
      }

      this.cleanUploadTemplate();

      this.deactivateDrawing();
      document.querySelector('.m-geometrydrawihm').appendChild(this.downloadingTemplate);
    } else {
      M.dialog.info(getValue('exception.emptylater'));
    }
  }

  /**
   * Removes upload template and reboots its options.
   * @public
   * @function
   * @api
   */
  cleanUploadTemplate() {
    if (document.querySelector('#geometrydrawihm-uploading') !== null) {
      document.querySelector('.m-geometrydrawihm').removeChild(this.uploadingTemplate);
      this.uploadingTemplate.querySelector('#geometrydrawihm-uploading>input').value = '';
      this.uploadingTemplate.querySelector('#geometrydrawihm-uploading>p#fileInfo').innerHTML = '';
      this.file_ = '';
      this.loadBtn_.setAttribute('disabled', 'disabled');
    }
  }

  /**
   * Opens upload template
   * @public
   * @function
   * @api
   */
  openUploadOptions() {
    if (this.isEditionActive) {
      this.deactivateEdition();
      document.querySelector('#otherBtns>#edit').classList.remove('activeTool');
    }
    if (document.querySelector('#downloadFormat') !== null) {
      document.querySelector('.m-geometrydrawihm').removeChild(this.downloadingTemplate);
    }
    this.deactivateDrawing();
    document.querySelector('.m-geometrydrawihm').appendChild(this.uploadingTemplate);
    this.loadBtn_.setAttribute('disabled', 'disabled');
  }

  /**
   * Parses geojsonLayer removing last item on every coordinate (NaN)
   * before converting the layer to kml.
   * @public
   * @function
   * @api
   * @param {*} geojsonLayer - geojson layer with drawn features
   */
  fixGeojsonKmlBug(geojsonLayer) {
    const newGeojsonLayer = geojsonLayer;
    const features = newGeojsonLayer.features;
    features.forEach((feature) => {
      switch (feature.geometry.type) {
        case 'Point':
          if (Number.isNaN(feature.geometry.coordinates[feature.geometry.coordinates.length - 1])) {
            feature.geometry.coordinates.pop();
          }
          break;
        case 'LineString':
          if (Number
            .isNaN(feature.geometry.coordinates[0][feature.geometry.coordinates[0].length - 1])) {
            feature.geometry.coordinates.map((line) => { return line.pop(); });
          }
          break;
        case 'Poylgon':
        case 'MultiPolygon':
          feature.geometry.coordinates.forEach((coord) => {
            if (feature.geometry.type === 'Polygon' &&
              Number.isNaN(coord[0][coord[0].length - 1])) {
              coord.map((c) => {
                c.pop();
                return c;
              });
            } else if (feature.geometry.type === 'MultiPolygon' &&
              Number.isNaN(coord[0][0][coord[0][0].length - 1])) {
              coord.forEach((coordsArray) => {
                coordsArray.map((c) => {
                  c.pop();
                  return c;
                });
              });
            }
          });
          break;
        default:
      }
    });

    newGeojsonLayer.features = features;
    return newGeojsonLayer;
  }

  /**
   * Parses geojson before shp download.
   * Changes geometry type to simple when necessary and removes one pair of brackets.
   * @public
   * @function
   * @api
   * @param {*} geojsonLayer - geojson layer with drawn and uploaded features
   */
  parseGeojsonForShp(geojsonLayer) {
    const newGeoJson = geojsonLayer;
    const newFeatures = [];

    geojsonLayer.features.forEach((originalFeature) => {
      const featureType = originalFeature.geometry.type;

      if (featureType.match(/^Multi/)) {
        const features = originalFeature.geometry.coordinates
          .map((simpleFeatureCoordinates, idx) => {
            const newFeature = {
              type: 'Feature',
              id: `${originalFeature.id}${idx}`,
              geometry: {
                type: '',
                coordinates: simpleFeatureCoordinates,
              },
              properties: {},
            };
            switch (featureType) {
              case 'MultiPoint':
                newFeature.geometry.type = 'Point';
                break;
              case 'MultiLineString':
                newFeature.geometry.type = 'LineString';
                break;
              case 'MultiPolygon':
                newFeature.geometry.type = 'Polygon';
                break;
              default:
            }
            return newFeature;
          });
        newFeatures.push(...features);
      } else {
        newFeatures.push(originalFeature);
      }
    });

    newGeoJson.features = newFeatures;
    for (let i = 0; i < newGeoJson.features.length; i += 1) {
      delete newGeoJson.features[i].id;
    }
    return newGeoJson;
  }

  /**
   * Creates vector layer copy of __draw__ layer excluding text features.
   * @public
   * @function
   * @api
   * @returns {M.layer.Vector}
   */
  newNoTextLayer() {
    const newLayer = new M.layer.Vector({ name: 'copia' });
    const noTextFeatures = this.drawLayer.getFeatures();
    newLayer.addFeatures(noTextFeatures);
    return newLayer;
  }

  /**
   * Create KML file
   * @public
   * @function
   * @api
   * @returns {M.layer.Vector}
   */
  GetKMLFromFeatures(features) {
    let kml = '<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="https://www.google.com/kml/ext/2.2" xmlns:xsi="https://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="https://www.opengis.net/kml/2.2 https://developers.google.com/kml/schema/kml22gx.xsd">';
    features.forEach((fea) => {
      const type = fea.getGeometry().type;
      const code = ol.proj.get(this.map_.getProjection().code);
      const coor = fea.getGeometry().coordinates;
      let coordinatesTransform = '';
      let coordinatesInner = '';
      const coorInnerArray = [];
      if (type === 'LineString') {
        for (let i = 0; i < coor.length; i += 1) {
          const h = ol.proj.transform(coor[i], code, 'EPSG:4326');
          coordinatesTransform = coordinatesTransform.concat(h);
        }
      } else if (type === 'Polygon' || type === 'MultiPolygon') {
        for (let i = 0; i < coor.length; i += 1) {
          for (let x = 0; x < coor[i].length; x += 1) {
            const h = ol.proj.transform(coor[i][x], code, 'EPSG:4326');
            if (i === 0) {
              coordinatesTransform = coordinatesTransform.concat(h);
            } else {
              coordinatesInner = coordinatesInner.concat(h);
            }
          }
          if (coordinatesInner !== '') {
            coorInnerArray.push(coordinatesInner);
            coordinatesInner = '';
          }
        }
      } else {
        coordinatesTransform = ol.proj.transform(coor, code, 'EPSG:4326');
      }
      const icon = fea.getStyle().getOptions().icon;
      if (type === 'Point' && icon !== undefined) {
        kml = kml.concat(`<Placemark id="${fea.getId()}"><name>${fea.getStyle().getOptions().label.text}</name><styleUrl>#icon</styleUrl><Style id="icon"><IconStyle><Icon><href>${fea.getStyle().getOptions().icon.src}</href></Icon></IconStyle></Style><Point><coordinates>${coordinatesTransform[0]},${coordinatesTransform[1]}</coordinates></Point></Placemark>`);
      } else if (type === 'Point') {
        const text = fea.getStyle().getOptions().label ? fea.getStyle().getOptions().label.text : '';
        kml = kml.concat(`<Placemark id="${fea.getId()}"><name>${text}</name><Style><color>501400AA</color></Style><Point><coordinates>${coordinatesTransform[0]},${coordinatesTransform[1]}</coordinates></Point></Placemark>`);
      } else if (type === 'LineString') {
        const color = fea.getStyle().getOptions().stroke.color;
        let colorkml = 'ff';
        const r = color.substring(1, 3);
        const g = color.substring(3, 5);
        const b = color.substring(5, 7);
        colorkml = colorkml.concat(b, g, r);
        kml = kml.concat(`<Placemark id="${fea.getId()}"><Style><LineStyle><color>${colorkml}</color><width>${fea.getStyle().getOptions().stroke.width}</width></LineStyle></Style><LineString><coordinates>${coordinatesTransform}</coordinates></LineString></Placemark>`);
      } else if (type === 'Polygon' || type === 'MultiPolygon') {
        const lengthHole = coorInnerArray.length;
        const color = fea.getStyle().getOptions().stroke.color;
        let colorkml = 'ff';
        const r = color.substring(1, 3);
        const g = color.substring(3, 5);
        const b = color.substring(5, 7);
        colorkml = colorkml.concat(b, g, r);
        kml = kml.concat(`<Placemark id="${fea.getId()}"><Style><LineStyle><color>${colorkml}</color><width>${fea.getStyle().getOptions().stroke.width}</width></LineStyle><PolyStyle><color>33633019</color></PolyStyle></Style><Polygon><outerBoundaryIs>
        <LinearRing>
          <coordinates>
          ${coordinatesTransform}
          </coordinates>
        </LinearRing>
      </outerBoundaryIs>`);
        for (let i = 0; i < lengthHole; i += 1) {
          kml = kml.concat(`<innerBoundaryIs><LinearRing><coordinates>${coorInnerArray[i]}</coordinates></LinearRing></innerBoundaryIs>`);
        }
        kml = kml.concat('</Polygon></Placemark>');
      }
    });
    kml = kml.concat('</kml>');
    return kml;
  }

  /**
   * Downloads draw layer as GeoJSON, kml or gml.
   * @public
   * @function
   * @api
   */
  downloadLayer() {
    const fileName = 'misgeometrias';
    const downloadFormat = this.downloadingTemplate.querySelector('select').value;
    const noTextLayer = this.newNoTextLayer();
    noTextLayer.setVisible(false);
    this.map.addLayers(noTextLayer);
    const geojsonLayer = this.toGeoJSON(noTextLayer, downloadFormat);
    this.map.removeLayers(noTextLayer);
    const noTextFeatures = this.drawLayer.getFeatures();
    const kml = this.GetKMLFromFeatures(noTextFeatures);
    let arrayContent;
    let mimeType;
    let extensionFormat;

    switch (downloadFormat) {
      case 'geojson':
        arrayContent = JSON.stringify(geojsonLayer);
        mimeType = 'json';
        extensionFormat = 'geojson';
        break;
      case 'kml':
        arrayContent = kml;
        mimeType = 'xml';
        extensionFormat = 'kml';
        break;
      case 'shp':
        const json = this.parseGeojsonForShp(geojsonLayer);
        const options = {
          folder: fileName,
          types: {
            point: 'puntos',
            polygon: 'poligonos',
            polyline: 'lineas',
          },
        };
        shpWrite.download(json, options);
        break;
      default:
        M.dialog.error(getValue('exception.ficherosel'));
        break;
    }

    if (downloadFormat !== 'shp') {
      const url = window.URL.createObjectURL(new window.Blob([arrayContent], {
        type: `application/${mimeType}`,
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.${extensionFormat}`);
      document.body.appendChild(link);
      link.click();
    }

    document.querySelector('.m-geometrydrawihm').removeChild(this.downloadingTemplate);
  }

  addFlag() {
    const inputpos = this.textDrawTemplateihm.querySelector('#textContentpos').value;
    const selectLocation = document.querySelector('#textdrawtoolsihm #formatihm');
    const posicion = inputpos.trim().split(',');
    if (selectLocation.value === 'grados') {
      if (inputpos !== '') {
        this.pos = [Number(posicion[1]), Number(posicion[0])];
      }
    } else {
      const lat = posicion[0].split(' ');
      const lon = posicion[1].split(' ');
      let latitud = Number(lat[0]) + (Number(lat[1] / 60));
      latitud = (lat[2].trim() === 'N' || lat[2].trim() === 'n') ? latitud : -latitud;
      let longitud = Number(lon[0]) + (Number(lon[1]) / 60);
      longitud = (lon[2].trim() === 'E' || lon[2].trim() === 'e') ? longitud : -longitud;
      this.pos = [longitud, latitud];
    }
    const code = this.map_.getProjection().code;
    if (this.pos !== undefined) {
      const coor = ol.proj.transform(this.pos, 'EPSG:4326', code);
      const num = Math.random();
      const feature = new M.Feature(`${num}`, {
        type: 'Feature',
        properties: { flag: this.flagcolor },
        geometry: {
          type: 'Point',
          coordinates: coor,
        },
      });
      this.drawLayer.addFeatures(feature);
      this.updateGlobalInputBandera();
      feature.setStyle(new M.style.Point({
        label: {
          text: this.textContentihm,
          align: M.style.align.LEFT,
          color: this.flagcolor,
        },
        icon: {
          src: this.flagIcon,
          anchor: [0.5, 0.6],
          scale: 0.5,
        },
      }));
      this.map.setCenter(coor);
      this.map.setZoom(20);
    }
  }

  /**
   * Deletes all drawn features.
   * @public
   * @function
   * @api
   */
  deleteDrawnFeatures() {
    this.deactivateDrawing();
    this.deactivateSelection();
    this.drawLayer.removeFeatures(this.drawLayer.getFeatures());
    this.feature = undefined;
    this.geometry = undefined;
    this.selectionLayer.removeFeatures([this.emphasis]);
    if (document.querySelector('#drawingtools #featureInfo') !== null) {
      document.querySelector('#drawingtools #featureInfo').style.display = 'none';
    }
    if (document.querySelector('#otherBtns>#edit') !== null) {
      document.querySelector('#otherBtns>#edit').classList.remove('activeTool');
    }
    if (document.querySelector('.m-geometrydrawihm>#downloadFormat') !== null) {
      document.querySelector('.m-geometrydrawihm').removeChild(this.downloadingTemplate);
    }
    this.cleanUploadTemplate();
  }

  /**
   * Deletes all flags.
   * @public
   * @function
   * @api
   */
  cleanFeaturesihm() {
    const featuresLength = this.drawLayer.getFeatures().length;
    const featur = this.drawLayer.getFeatures()[featuresLength - 1];
    this.drawLayer.removeFeatures(featur);
    this.textDrawTemplateihm.querySelector('#textContentihm').value = '';
    this.textDrawTemplateihm.querySelector('#textContentpos').value = '';
    this.deactivateDrawing();
    this.deactivateSelection();
  }

  /**
   * Deletes selected geometry.
   * @public
   * @function
   * @api
   */
  deleteSingleFeature() {
    const editionMode = this.isEditionActive;
    this.drawLayer.removeFeatures([this.feature]);
    this.feature = undefined;
    this.geometry = undefined;
    this.selectionLayer.removeFeatures([this.emphasis]);
    this.deactivateDrawing();
    this.deactivateSelection();
    if (editionMode) this.getImpl().activateSelection();
  }

  /**
   * Gets given feature thickness.
   * @public
   * @function
   * @api
   */
  getFeatureThickness() {
    const feature = this.feature;
    if (feature.getGeometry().type === 'Point' || feature.getGeometry().type === 'MultiPoint') {
      return feature.getStyle().get('radius');
    }
    return feature.getStyle().get('stroke.width');
  }

  /**
   * Gets given feature color.
   * @public
   * @function
   * @api
   */
  getFeatureColor() {
    const feature = this.feature;
    if (feature.getGeometry().type === 'Point' || feature.getGeometry().type === 'MultiPoint') {
      return feature.getStyle().get('fill.color');
    }
    return feature.getStyle().get('stroke.color');
  }

  /**
   * This function compares controls
   *
   * @public
   * @function
   * @param {M.Control} control to compare
   * @api stable
   */
  equals(control) {
    return control instanceof GeometryDrawControlIhm;
  }

  /* Layer upload */

  /**
   * Changes selected file.
   * @public
   * @function
   * @api
   * @param {Event} evt - file input change event
   * @param {File} file - selected file on file input
   */
  changeFile(evt, file) {
    this.file_ = file;
    this.loadBtn_.setAttribute('disabled', 'disabled');
    if (!M.utils.isNullOrEmpty(file)) {
      if (file.size > 20971520) {
        M.dialog.info(getValue('exception.maxfichero'));
        this.file_ = null;
        document.querySelector('#fileInfo').innerHTML = '';
      } else {
        this.loadBtn_.removeAttribute('disabled');
        document.querySelector('#fileInfo').innerHTML = `Fichero seleccionado: ${file.name}`;
      }
    }
  }

  /**
   * Loads vector layer features on __draw__ (Mapea) layer.
   * @public
   * @function
   * @api
   */
  loadLayer() {
    // eslint-disable-next-line no-bitwise
    const fileExt = this.file_.name.slice(this.file_.name.lastIndexOf('.') + 1).toLowerCase();
    const fileReader = new window.FileReader();
    fileReader.addEventListener('load', (e) => {
      try {
        let features = [];
        if (fileExt === 'zip') {
          // In case of shp group, this unites features
          const geojsonArray = [].concat(shp.parseZip(fileReader.result));
          geojsonArray.forEach((geojson) => {
            const localFeatures = this.getImpl()
              .loadGeoJSONLayer(geojson);
            if (localFeatures) {
              features = features.concat(localFeatures);
            }
          });
        } else if (fileExt === 'kml') {
          features = this.getImpl()
            .loadKMLLayer('nombrelayer', fileReader.result, true, fileExt);
        } else if (fileExt === 'gpx') {
          features = this.getImpl()
            .loadGPXLayer(fileReader.result);
        } else if (fileExt === 'geojson') {
          features = this.getImpl()
            .loadGeoJSONLayer(fileReader.result, fileExt);
        } else {
          M.dialog.error(getValue('exception.errorfichero'));
          return;
        }
        if (features.length === 0) {
          M.dialog.info(getValue('exception.nodetectado'));
        } else {
          this.getImpl().centerFeatures(features);
          this.emphasizeSelectedFeature();
        }
      } catch (error) {
        M.dialog.error(getValue('exception.comprobar'));
      }
    });
    if (fileExt === 'zip') {
      fileReader.readAsArrayBuffer(this.file_);
    } else if (fileExt === 'kml' || fileExt === 'gpx' || fileExt === 'geojson') {
      fileReader.readAsText(this.file_);
    } else {
      M.dialog.error(getValue('exception.insertado'));
    }
  }

  /**
   * Turns GeometryCollection features into single geometry features.
   * @public
   * @function
   * @api
   * @param {Array<M.Feature>} features
   */
  geometryCollectionParse(features) {
    const parsedFeatures = [];
    features.forEach((feature) => {
      if (feature.getGeometry().type === 'GeometryCollection') {
        const geometries = feature.getGeometry().geometries;
        geometries.forEach((geometry) => {
          const num = Math.random();
          const newFeature = new M.Feature(`mf${num}`, {
            type: 'Feature',
            id: `gf${num}`,
            geometry: {
              type: geometry.type,
              coordinates: geometry.coordinates,
            },
          });
          parsedFeatures.push(newFeature);
        });
      } else {
        parsedFeatures.push(feature);
      }
    });
    return parsedFeatures;
  }

  /* TO GEOJSON WITH BUG FIXED */

  /**
   * This function gets the geojson representation of the layer
   * @function
   * @api
   */
  toGeoJSON(layer, downloadFormat) {
    const code = this.map_.getProjection().code;
    const featuresAsJSON = layer.getFeatures().map(feature => ({
      feature: feature.getGeoJSON(),
      name: feature.getStyle().getOptions().label ? feature.getStyle().getOptions().label : '',
      icon: feature.getStyle().getOptions().icon ? feature.getStyle().getOptions().icon : '',
      style: feature.getStyle().getOptions(),
    }));
    return { type: 'FeatureCollection', features: this.geojsonTo4326(featuresAsJSON, code, downloadFormat) };
  }

  /**
   * Creates GeoJSON feature from a previous feature and a new set of coordinates.
   * @public
   * @function
   * @api
   * @param {GeoJSON Feature} previousFeature
   * @param {Array} coordinates
   */
  createGeoJSONFeature(previousFeature, coordinates) {
    return {
      ...previousFeature,
      geometry: {
        type: previousFeature.geometry.type,
        coordinates,
      },
    };
  }

  /**
   * Converts features coordinates on geojson format to 4326.
   * Add properties
   * @public
   * @function
   */
  geojsonTo4326(featuresAsJSON, codeProjection, downloadFormat) {
    const jsonResult = [];
    featuresAsJSON.forEach((featureAsJSON) => {
      const coordinates = featureAsJSON.feature.geometry.coordinates;
      let newCoordinates = [];
      switch (featureAsJSON.feature.geometry.type) {
        case 'Point':
          newCoordinates = this.getImpl().getTransformedCoordinates(codeProjection, coordinates);
          break;
        case 'MultiPoint':
          for (let i = 0; i < coordinates.length; i += 1) {
            const newDot = this
              .getImpl().getTransformedCoordinates(codeProjection, coordinates[i]);
            newCoordinates.push(newDot);
          }
          break;
        case 'LineString':
          for (let i = 0; i < coordinates.length; i += 1) {
            const newDot = this.getImpl().getTransformedCoordinates(
              codeProjection,
              coordinates[i],
            );
            newCoordinates.push(newDot);
          }
          break;
        case 'MultiLineString':
          for (let i = 0; i < coordinates.length; i += 1) {
            const newLine = [];
            for (let j = 0; j < coordinates[i].length; j += 1) {
              const newDot = this
                .getImpl().getTransformedCoordinates(codeProjection, coordinates[i][j]);
              newLine.push(newDot);
            }
            newCoordinates.push(newLine);
          }
          break;
        case 'Polygon':
          for (let i = 0; i < coordinates.length; i += 1) {
            const newPoly = [];
            for (let j = 0; j < coordinates[i].length; j += 1) {
              const newDot = this
                .getImpl().getTransformedCoordinates(codeProjection, coordinates[i][j]);
              newPoly.push(newDot);
            }
            newCoordinates.push(newPoly);
          }
          break;
        case 'MultiPolygon':
          for (let i = 0; i < coordinates.length; i += 1) {
            const newPolygon = [];
            for (let j = 0; j < coordinates[i].length; j += 1) {
              const newPolygonLine = [];
              for (let k = 0; k < coordinates[i][j].length; k += 1) {
                const newDot = this
                  .getImpl().getTransformedCoordinates(codeProjection, coordinates[i][j][k]);
                newPolygonLine.push(newDot);
              }
              newPolygon.push(newPolygonLine);
            }
            newCoordinates.push(newPolygon);
          }
          break;
        default:
      }
      const jsonFeature = this.createGeoJSONFeature(featureAsJSON.feature, newCoordinates);
      if (downloadFormat === 'geojson') {
        if (jsonFeature.geometry.type === 'Point' && featureAsJSON.icon !== '') {
          jsonFeature.properties = {
            image_ihm: featureAsJSON.icon.src ? featureAsJSON.icon.src : '',
            anchor_ihm: M.utils.isArray(featureAsJSON.icon.anchor) ?
              featureAsJSON.icon.anchor : [0, 0],
            scale_ihm: featureAsJSON.icon.scale ? featureAsJSON.icon.scale : '',
            name_ihm: featureAsJSON.name.text ? featureAsJSON.name.text : '',
            align_ihm: featureAsJSON.name.align ? featureAsJSON.name.align : '',
            color_ihm: featureAsJSON.name.color ? featureAsJSON.name.color : '',
          };
        } else if (jsonFeature.geometry.type === 'Point' || jsonFeature.geometry.type === 'MultiPoint') {
          jsonFeature.properties = {
            fill_color_ihm: featureAsJSON.style.fill.color ? featureAsJSON.style.fill.color : '',
            radius_ihm: featureAsJSON.style.radius ? featureAsJSON.style.radius : '',
            stroke_color_ihm: featureAsJSON.style.stroke.color ? featureAsJSON.style.stroke.color : '',
            stroke_width_ihm: featureAsJSON.style.stroke.width ? featureAsJSON.style.stroke.width : '',
            name_ihm: featureAsJSON.name.text ? featureAsJSON.name.text : '',
            color_ihm: featureAsJSON.name.color ? featureAsJSON.name.color : '',
            font_ihm: featureAsJSON.name.font ? featureAsJSON.name.font : '',

          };
        } else if (jsonFeature.geometry.type === 'LineString' || jsonFeature.geometry.type === 'MultiLineString') {
          jsonFeature.properties = {
            stroke_color_ihm: featureAsJSON.style.stroke.color ? featureAsJSON.style.stroke.color : '',
            stroke_width_ihm: featureAsJSON.style.stroke.width ? featureAsJSON.style.stroke.width : '',
          };
        } else if (jsonFeature.geometry.type === 'Polygon' || jsonFeature.geometry.type === 'MultiPolygon') {
          jsonFeature.properties = {
            fill_color_ihm: featureAsJSON.style.fill.color ? featureAsJSON.style.fill.color : '',
            fill_opacity_ihm: featureAsJSON.style.fill.opacity ? featureAsJSON.style.fill.opacity : '',
            stroke_color_ihm: featureAsJSON.style.stroke.color ? featureAsJSON.style.stroke.color : '',
            stroke_width_ihm: featureAsJSON.style.stroke.width ? featureAsJSON.style.stroke.width : '',
          };
        }
      }
      jsonResult.push(jsonFeature);
    });
    return jsonResult;
  }

  /**
   * Check whether the value is a JavaScript number.
   *
   * First checks typeof, then self-equality to make sure it is
   * not NaN, then Number.isFinite() to check for Infinity.
   *
   * @public
   * @function
   * @param {*} value - The value to check
   * @return {boolean} Whether that value is a number
   */
  isNumber(value) {
    if (typeof value !== 'number') return false;
    if (value !== Number(value)) return false;
    if (Number.isFinite(value) === false) return false;
    return true;
  }

  /**
   * Modifies drawing tools, updates inputs, emphasizes selection
   * and shows feature info on select.
   * @public
   * @function
   * @api
   * @param {Event}
   */
  onSelect(e) {
    const MFeatures = this.drawLayer.getFeatures();
    const olFeature = e.target.getFeatures().getArray()[0];

    this.feature = MFeatures.filter(f => f.getImpl().getOLFeature() ===
      olFeature)[0] || undefined;

    this.geometry = this.feature.getGeometry().type;

    if (document.querySelector('.m-geometrydrawihm #drawingtools') !== null) {
      document.querySelector('.m-geometrydrawihm').removeChild(this.drawingTools);
    } else if (document.querySelector('.m-geometrydrawihm #textdrawtools') !== null) {
      document.querySelector('.m-geometrydrawihm').removeChild(this.textDrawTemplate);
    }

    // if selected feature is a text feature
    if (this.geometry === 'Point' &&
      this.feature.getStyle().get('label') !== undefined && this.feature.getStyle().get('icon') === undefined) {
      document.querySelector('.m-geometrydrawihm').appendChild(this.textDrawTemplate);
      document.querySelector('.m-geometrydrawihm>#textdrawtools button').style.display = 'block';
    } else if (this.feature.getStyle().get('icon') === undefined) {
      document.querySelector('.m-geometrydrawihm').appendChild(this.drawingTools);
      document.querySelector('.m-geometrydrawihm>#drawingtools button').style.display = 'block';
    }
    this.updateInputValues();
    this.emphasizeSelectedFeature();
    this.showFeatureInfo();
  }

  /**
   * Emphasizes selection and shows feature info after feature is modified.
   * @public
   * @function
   * @api
   */
  onModify() {
    this.emphasizeSelectedFeature();
    this.showFeatureInfo();
  }

  /**
   * Sets style for features and adds
   * features to drawLayer.
   * @function
   * @public
   * @api
   * @param {Array<M.Feature>} features
   * @return {Array<M.Feature>}
   */
  featuresLoader(features, type) {
    const newFeatures = features;
    if (type === 'geojson') {
      for (let i = 0; i < newFeatures.length; i += 1) {
        const typeFeature = newFeatures[i].getGeometry().type;
        const atributes = newFeatures[i].getAttributes();
        if (typeFeature === 'Point' && atributes.image_ihm !== undefined) {
          newFeatures[i].setStyle(new M.style.Point({
            label: {
              text: atributes.name_ihm,
              align: M.style.align.LEFT,
              color: atributes.color_ihm,
            },
            icon: {
              src: atributes.image_ihm,
              anchor: atributes.anchor_ihm,
              scale: atributes.scale_ihm,
            },
          }));
        } else if (typeFeature === 'Point' || typeFeature === 'MultiPoint') {
          newFeatures[i].setStyle(new M.style.Point({
            radius: atributes.radius_ihm,
            label: {
              text: atributes.name_ihm ? atributes.name_ihm : '',
              color: atributes.color_ihm ? atributes.color_ihm : '',
              font: atributes.font_ihm ? atributes.font_ihm : '',
            },
            fill: {
              color: atributes.fill_color_ihm,
            },
            stroke: {
              color: atributes.stroke_color_ihm,
              width: atributes.stroke_width_ihm,
            },
          }));
        } else if (typeFeature === 'LineString' || typeFeature === 'MultiLineString') {
          newFeatures[i].setStyle(new M.style.Line({
            stroke: {
              color: atributes.stroke_color_ihm,
              width: atributes.stroke_width_ihm,
            },
          }));
        } else if (typeFeature === 'Polygon' || typeFeature === 'MultiPolygon') {
          newFeatures[i].setStyle(new M.style.Polygon({
            fill: {
              color: atributes.fill_color_ihm,
              opacity: atributes.fill_opacity_ihm,
            },
            stroke: {
              color: atributes.stroke_color_ihm,
              width: atributes.stroke_width_ihm,
            },
          }));
        }
      }
    }
    // // Avoids future conflicts if different layers are loaded
    // newFeatures = this.deleteFeatureAttributes(newFeatures);

    // for (let i = 0; i < newFeatures.length; i += 1) {
    //   this.setFeatureStyle(newFeatures[i], newFeatures[i].getGeometry().type);
    // }

    this.drawLayer.addFeatures(newFeatures);
    return newFeatures;
  }

  /**
   * Deactivates selection mode.
   * @public
   * @function
   * @api
   */
  deactivateSelection() {
    if (this.drawLayer) {
      if (document.querySelector('.m-geometrydrawihm #drawingtools')) {
        document.querySelector('.m-geometrydrawihm>#drawingtools button').style.display = 'none';
        document.querySelector('.m-geometrydrawihm').removeChild(this.drawingTools);
      }
      if (document.querySelector('.m-geometrydrawihm #textdrawtools')) {
        document.querySelector('.m-geometrydrawihm>#textdrawtools button').style.display = 'none';
        document.querySelector('.m-geometrydrawihm').removeChild(this.textDrawTemplate);
      }

      this.hideTextPoint();
      this.feature = undefined;
      this.geometry = undefined;
      this.emphasizeSelectedFeature();

      this.getImpl().removeEditInteraction();
      this.getImpl().removeSelectInteraction();
    }
  }
}
